import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { getAsset } from "../utilities/assets.utils";
import { HB_BOARD_API } from "../utilities/constants.utils";

/**
 * Ask user to enter board URL and fetch the board info.
 * @param {GetBoardInfo.propTypes} props
 */
export const GetBoardInfo = (props) => {
	const [url, setUrl] = useState("");

	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);

	const onUrlSubmit = async (event) => {
		event?.preventDefault();
		try {
			props.onData();

			const savedConfig =
				props.savedConfig && !event ? props.savedConfig : null;

			const savedBoardURL = `https://hootboard.com/b/${savedConfig?.boardId}/`;

			const urlMatchRegex = new RegExp(
				/https?:\/\/(www\.|)hootboard\.com\//gm
			);

			const testURL = savedConfig ? savedBoardURL : url;

			if (!urlMatchRegex.test(testURL)) {
				setHasError("Invalid URL!");
				return;
			}

			// If the board URL contains a custom URL, this will be a string. Or a number.
			const boardIdentifier = (testURL + "/")
				.match(/\.com\/[\s\S]*\/?\d/)[0]
				.replace(/\/b\/|\/|.com/g, "");

			// We will try to convert the identifier to a number.
			const hasBoardId = !isNaN(+boardIdentifier);
			const customURL = hasBoardId ? "" : boardIdentifier;

			// We have to pass 0 incase the board URL is a custom URL.
			const boardId = hasBoardId ? +boardIdentifier : 0;

			setHasError();
			setIsLoading(true);

			const apiResponse = await fetch(HB_BOARD_API + customURL, {
				headers: {
					"x-board-id": boardId,
				},
			});
			try {
				// If json parse fails, it means the API returned an error.
				const jsonData = await apiResponse.json();
				if (!jsonData) {
					throw new Error();
				}
				props.onData(jsonData);

				// once the data is fetched, scroll the page smoothly to the bottom.
				setTimeout(() => {
					window.scrollTo({
						left: 0,
						top: document.body.scrollHeight,
						behavior: "smooth",
					});
				}, 100);
			} catch (error) {
				setHasError("Can't find the HootBoard!");
				return;
			}
		} catch (error) {
			console.error(error);
			setHasError("Yikes! Something went wrong. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		props.onLoader(isLoading);
	}, [isLoading]);

	useEffect(() => {
		if (props.savedConfig) {
			onUrlSubmit();
		}
	}, [props.savedConfig]);

	const isSavedBoardLoading = props.savedConfig && isLoading;

	return (
		<div className="bg-plugin-2-light flex flex-col items-center rounded-sm p-8 mb-6">
			<img
				src={getAsset("enter-link.png")}
				alt=""
				className="w-16 mb-2"
			/>
			<div className="text-xl font-semibold mt-2">
				{isSavedBoardLoading
					? "Please wait..."
					: "Paste your HootBoard link"}
			</div>
			{isSavedBoardLoading ? (
				<div className="mt-3 text-center leading-6">
					we are fetching your HootBoard details
				</div>
			) : (
				<>
					<div className="mt-3 mb-6 text-center leading-6">
						The link of your HootBoard should look like
						<br />
						https://www.hootboard.com/b/xxxxx/your_board_name
					</div>
					<form onSubmit={onUrlSubmit} className="flex">
						<Input
							placeholder="Enter your link"
							onChange={setUrl}
							color="plugin-2"
							value={url}
							isDisabled={isLoading}
						/>
						<span className="w-2"></span>
						<Button
							label={isLoading ? "please wait..." : "Apply"}
							isDisabled={isLoading}
							type="submit"
							color="plugin-2"
						/>
					</form>
				</>
			)}
			{hasError && (
				<div className="text-center rounded-md text-red-600 font-bold mt-6">
					{hasError}
				</div>
			)}
		</div>
	);
};

GetBoardInfo.defaultProps = {
	onLoader: () => {},
	onData: () => {},
};

GetBoardInfo.propTypes = {
	/**
	 * Loading status updates. Return boolean.
	 */
	onLoader: PropTypes.func,
	/**
	 * Already saved embed data.
	 */
	savedConfig: PropTypes.object,
	/**
	 * Once the board information is fetched, this function will be called with new board info.
	 */
	onData: PropTypes.func.isRequired,
};
